/**
 * Check-Out Dialog Component
 * Handles guest check-out confirmation with folio display and print/email options
 */
import React, { useState, useEffect } from 'react';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from '../ui/dialog';
import { Button } from '../ui/button';
import { Alert, AlertDescription } from '../ui/alert';
import { Separator } from '../ui/separator';
import { Badge } from '../ui/badge';
import { Card, CardContent, CardHeader, CardTitle } from '../ui/card';
import { LogOut, DollarSign, Printer, Mail, Receipt, Loader2, CheckCircle } from 'lucide-react';
import { toast } from 'sonner';
import { BACKEND_URL } from '@/lib/backendUrl';


export const CheckOutDialog = ({
  open,
  onOpenChange,
  guest,
  onConfirm,
  loading,
  hotelId
}) => {
  const [folioData, setFolioData] = useState(null);
  const [loadingFolio, setLoadingFolio] = useState(false);
  const [emailingSending, setEmailingSending] = useState(false);

  useEffect(() => {
    if (open && guest && hotelId) {
      loadFolioData();
    }
  }, [open, guest, hotelId]);

  const loadFolioData = async () => {
    const guestEmail = guest?.guest_email || guest?.email;
    
    if (!guestEmail) {
      return;
    }

    try {
      setLoadingFolio(true);
      const response = await fetch(
        `${BACKEND_URL}/api/pms/${hotelId}/guest-folio?email=${encodeURIComponent(guestEmail)}`
      );

      if (!response.ok) {
        throw new Error('Failed to fetch folio');
      }

      const data = await response.json();
      setFolioData(data);
    } catch (error) {
      console.error('Error loading folio:', error);
    } finally {
      setLoadingFolio(false);
    }
  };

  const handlePrint = () => {
    // Create printable folio content
    const printWindow = window.open('', '_blank');
    const folioHtml = generateFolioHTML();
    
    printWindow.document.write(folioHtml);
    printWindow.document.close();
    printWindow.focus();
    printWindow.print();
    
    toast.success('Folio sent to printer');
  };

  const handleEmail = async () => {
    const guestEmail = guest?.guest_email || guest?.email;
    
    if (!guestEmail) {
      toast.error('Guest email not found');
      return;
    }

    try {
      setEmailingSending(true);
      
      const response = await fetch(`${BACKEND_URL}/api/pms/${hotelId}/email-folio`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          guest_email: guestEmail,
          booking_id: guest.booking_id || guest.id
        })
      });

      if (!response.ok) {
        throw new Error('Failed to send email');
      }

      toast.success(`Folio emailed to ${guestEmail}`);
    } catch (error) {
      console.error('Error emailing folio:', error);
      toast.error('Failed to send folio email');
    } finally {
      setEmailingSending(false);
    }
  };

  const generateFolioHTML = () => {
    const currentBooking = folioData?.bookings?.find(b => b.id === (guest.booking_id || guest.id));
    const roomRate = currentBooking?.final_price / (guest.nights || 1);
    const taxRate = 0.15; // 15% tax
    const serviceFee = 25; // Fixed service fee
    
    // Calculate breakdown
    const roomSubtotal = roomRate * (guest.nights || 1);
    const taxAmount = roomSubtotal * taxRate;
    const grandTotal = roomSubtotal + taxAmount + serviceFee;
    
    return `
      <!DOCTYPE html>
      <html>
      <head>
        <title>Guest Folio - ${guest.guest_name}</title>
        <style>
          * { margin: 0; padding: 0; box-sizing: border-box; }
          body { 
            font-family: 'Segoe UI', system-ui, sans-serif;
            padding: 30px;
            background: #f5f5f5;
          }
          .folio-wrapper {
            max-width: 900px;
            margin: 0 auto;
            background: white;
            box-shadow: 0 10px 40px rgba(0,0,0,0.1);
          }
          .header {
            background: linear-gradient(135deg, #f59e0b 0%, #d97706 50%, #b45309 100%);
            color: white;
            padding: 50px 40px;
            box-shadow: 0 4px 20px rgba(245, 158, 11, 0.3);
          }
          .hotel-info {
            text-align: center;
          }
          .hotel-name {
            font-size: 32px;
            font-weight: 700;
            letter-spacing: 1px;
            margin-bottom: 8px;
          }
          .hotel-address {
            font-size: 13px;
            opacity: 0.9;
            margin-bottom: 20px;
          }
          .folio-title {
            font-size: 14px;
            font-weight: 600;
            letter-spacing: 3px;
            text-transform: uppercase;
            padding-top: 20px;
            border-top: 1px solid rgba(255,255,255,0.2);
            margin-top: 15px;
          }
          .content {
            padding: 40px;
          }
          .info-section {
            display: grid;
            grid-template-columns: 1fr 1fr;
            gap: 30px;
            margin-bottom: 40px;
            padding-bottom: 30px;
            border-bottom: 2px solid #e5e7eb;
          }
          .info-box {
            background: linear-gradient(135deg, #fef3c7 0%, #fde68a 100%);
            padding: 25px;
            border-radius: 12px;
            border-left: 5px solid #f59e0b;
            box-shadow: 0 2px 10px rgba(245, 158, 11, 0.1);
          }
          .info-title {
            font-size: 11px;
            font-weight: 700;
            color: #6b7280;
            text-transform: uppercase;
            letter-spacing: 1px;
            margin-bottom: 12px;
          }
          .info-item {
            display: flex;
            justify-content: space-between;
            padding: 6px 0;
            font-size: 13px;
          }
          .info-label {
            color: #6b7280;
            font-weight: 500;
          }
          .info-value {
            color: #1f2937;
            font-weight: 600;
          }
          .charges-section {
            margin: 30px 0;
          }
          .section-header {
            background: linear-gradient(135deg, #fef3c7 0%, #fde68a 100%);
            padding: 15px 25px;
            border-radius: 12px 12px 0 0;
            border-bottom: 3px solid #f59e0b;
          }
          .section-title {
            font-size: 13px;
            font-weight: 800;
            background: linear-gradient(135deg, #f59e0b 0%, #d97706 100%);
            -webkit-background-clip: text;
            -webkit-text-fill-color: transparent;
            background-clip: text;
            text-transform: uppercase;
            letter-spacing: 2px;
          }
          .charges-table {
            width: 100%;
            border-collapse: collapse;
          }
          .charges-table th {
            background: #fafafa;
            padding: 12px 20px;
            text-align: left;
            font-size: 11px;
            font-weight: 700;
            color: #6b7280;
            text-transform: uppercase;
            letter-spacing: 0.5px;
            border-bottom: 1px solid #e5e7eb;
          }
          .charges-table td {
            padding: 15px 20px;
            border-bottom: 1px solid #f3f4f6;
            font-size: 14px;
          }
          .charge-date {
            color: #6b7280;
            font-size: 12px;
          }
          .charge-desc {
            color: #1f2937;
            font-weight: 500;
          }
          .charge-detail {
            color: #9ca3af;
            font-size: 12px;
            margin-top: 2px;
          }
          .charge-amount {
            text-align: right;
            font-weight: 600;
            color: #1f2937;
          }
          .category-row {
            background: #f9fafb !important;
          }
          .category-row td {
            font-weight: 700;
            color: #374151;
            padding: 12px 20px;
            border-top: 2px solid #e5e7eb;
          }
          .summary-section {
            background: #fafafa;
            padding: 25px;
            border-radius: 8px;
            margin-top: 30px;
          }
          .summary-row {
            display: flex;
            justify-content: space-between;
            padding: 10px 0;
            font-size: 14px;
          }
          .summary-label {
            color: #6b7280;
            font-weight: 500;
          }
          .summary-value {
            font-weight: 600;
            color: #1f2937;
          }
          .subtotal-row {
            border-top: 1px solid #e5e7eb;
            padding-top: 12px !important;
            margin-top: 5px;
          }
          .total-row {
            background: linear-gradient(135deg, #f59e0b 0%, #d97706 100%);
            color: white;
            padding: 20px 25px !important;
            margin: 15px -25px -25px -25px;
            border-radius: 0 0 12px 12px;
            font-size: 19px;
            box-shadow: 0 -2px 10px rgba(245, 158, 11, 0.2);
          }
          .total-row .summary-label,
          .total-row .summary-value {
            color: white;
            font-weight: 700;
          }
          .payment-badge {
            display: inline-block;
            background: #10b981;
            color: white;
            padding: 6px 16px;
            border-radius: 20px;
            font-size: 11px;
            font-weight: 700;
            text-transform: uppercase;
            letter-spacing: 0.5px;
            margin-top: 15px;
          }
          .footer {
            background: linear-gradient(135deg, #78350f 0%, #92400e 100%);
            color: white;
            padding: 35px 40px;
            text-align: center;
          }
          .thank-you {
            font-size: 20px;
            font-weight: 700;
            margin-bottom: 12px;
            letter-spacing: 0.5px;
          }
          .footer-text {
            font-size: 12px;
            opacity: 0.95;
            line-height: 1.8;
          }
          @media print {
            body { background: white; padding: 0; }
            .folio-wrapper { box-shadow: none; }
          }
        </style>
      </head>
      <body>
        <div class="folio-wrapper">
          <div class="header">
            <div class="hotel-info">
              <div class="hotel-name">The Grand Marina Hotel</div>
              <div class="hotel-address">Dubai, UAE | contact@grandmarina.com | +971 4 XXX XXXX</div>
              <div class="folio-title">Guest Folio</div>
            </div>
          </div>

          <div class="content">
            <!-- Guest & Stay Information -->
            <div class="info-section">
              <div class="info-box">
                <div class="info-title">Guest Information</div>
                <div class="info-item">
                  <span class="info-label">Name</span>
                  <span class="info-value">${guest.guest_name}</span>
                </div>
                <div class="info-item">
                  <span class="info-label">Email</span>
                  <span class="info-value">${folioData?.guest?.email || 'N/A'}</span>
                </div>
                <div class="info-item">
                  <span class="info-label">Phone</span>
                  <span class="info-value">${folioData?.guest?.phone || 'N/A'}</span>
                </div>
              </div>
              <div class="info-box">
                <div class="info-title">Stay Details</div>
                <div class="info-item">
                  <span class="info-label">Room Number</span>
                  <span class="info-value">${guest.assigned_room_number || 'N/A'}</span>
                </div>
                <div class="info-item">
                  <span class="info-label">Room Type</span>
                  <span class="info-value">${guest.room_type || 'N/A'}</span>
                </div>
                <div class="info-item">
                  <span class="info-label">Check-In</span>
                  <span class="info-value">${guest.check_in}</span>
                </div>
                <div class="info-item">
                  <span class="info-label">Check-Out</span>
                  <span class="info-value">${guest.check_out}</span>
                </div>
                <div class="info-item">
                  <span class="info-label">Nights</span>
                  <span class="info-value">${guest.nights} Nights</span>
                </div>
              </div>
            </div>

            <!-- Itemized Charges -->
            <div class="charges-section">
              <div class="section-header">
                <div class="section-title">Itemized Charges</div>
              </div>
              <table class="charges-table">
                <thead>
                  <tr>
                    <th style="width: 20%;">Date</th>
                    <th style="width: 60%;">Description</th>
                    <th style="width: 20%;">Amount</th>
                  </tr>
                </thead>
                <tbody>
                  <!-- Room Charges Category -->
                  <tr class="category-row">
                    <td colspan="3">ROOM CHARGES</td>
                  </tr>
                  ${Array.from({length: guest.nights || 1}, (_, i) => {
                    const date = new Date(guest.check_in);
                    date.setDate(date.getDate() + i);
                    return `
                      <tr>
                        <td class="charge-date">${date.toLocaleDateString()}</td>
                        <td>
                          <div class="charge-desc">Room ${guest.assigned_room_number || 'N/A'} - ${guest.room_type || 'N/A'}</div>
                          <div class="charge-detail">Night ${i + 1} of ${guest.nights}</div>
                        </td>
                        <td class="charge-amount">$${roomRate.toFixed(2)}</td>
                      </tr>
                    `;
                  }).join('')}
                  
                  <!-- Taxes & Fees Category -->
                  <tr class="category-row">
                    <td colspan="3">TAXES & FEES</td>
                  </tr>
                  <tr>
                    <td class="charge-date">${new Date().toLocaleDateString()}</td>
                    <td>
                      <div class="charge-desc">City Tourism Tax (15%)</div>
                      <div class="charge-detail">Applied to room charges</div>
                    </td>
                    <td class="charge-amount">$${taxAmount.toFixed(2)}</td>
                  </tr>
                  <tr>
                    <td class="charge-date">${new Date().toLocaleDateString()}</td>
                    <td>
                      <div class="charge-desc">Resort Service Fee</div>
                      <div class="charge-detail">WiFi, Pool, Gym Access</div>
                    </td>
                    <td class="charge-amount">$${serviceFee.toFixed(2)}</td>
                  </tr>
                  
                  <!-- Additional Charges Category (Example) -->
                  <tr class="category-row">
                    <td colspan="3">ADDITIONAL CHARGES</td>
                  </tr>
                  <tr>
                    <td class="charge-date">${new Date().toLocaleDateString()}</td>
                    <td>
                      <div class="charge-desc">No additional charges</div>
                      <div class="charge-detail">Minibar, vending, and other services</div>
                    </td>
                    <td class="charge-amount">$0.00</td>
                  </tr>
                </tbody>
              </table>
            </div>

            <!-- Summary -->
            <div class="summary-section">
              <div class="summary-row">
                <span class="summary-label">Room Charges Subtotal</span>
                <span class="summary-value">$${roomSubtotal.toFixed(2)}</span>
              </div>
              <div class="summary-row">
                <span class="summary-label">Tourism Tax (15%)</span>
                <span class="summary-value">$${taxAmount.toFixed(2)}</span>
              </div>
              <div class="summary-row">
                <span class="summary-label">Service Fee</span>
                <span class="summary-value">$${serviceFee.toFixed(2)}</span>
              </div>
              <div class="summary-row">
                <span class="summary-label">Additional Charges</span>
                <span class="summary-value">$0.00</span>
              </div>
              <div class="summary-row subtotal-row">
                <span class="summary-label">Subtotal</span>
                <span class="summary-value">$${grandTotal.toFixed(2)}</span>
              </div>
              <div class="summary-row total-row">
                <span class="summary-label">TOTAL AMOUNT</span>
                <span class="summary-value">$${grandTotal.toFixed(2)}</span>
              </div>
              <div style="text-align: center;">
                <div class="payment-badge">✓ PAID IN FULL</div>
              </div>
            </div>
          </div>

          <div class="footer">
            <div class="thank-you">Thank You for Staying With Us!</div>
            <div class="footer-text">
              We hope you enjoyed your stay at The Grand Marina Hotel.<br>
              For any questions regarding this folio, please contact our front desk.<br>
              Folio generated on ${new Date().toLocaleDateString()} at ${new Date().toLocaleTimeString()}
            </div>
          </div>
        </div>
      </body>
      </html>
    `;
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-w-3xl p-0 overflow-hidden" data-testid="checkout-dialog">
        {/* Modern Gradient Header */}
        <div className="bg-gradient-to-br from-orange-500 via-amber-500 to-yellow-600 text-white px-6 py-5">
          <div className="flex items-center gap-3">
            <div className="bg-orange-600 p-3 rounded-xl shadow-lg">
              <LogOut className="w-6 h-6 text-white" />
            </div>
            <div>
              <DialogTitle className="text-2xl font-bold text-white mb-1">
                Check Out Guest
              </DialogTitle>
              <p className="text-white/90 text-sm">Review folio and complete checkout</p>
            </div>
          </div>
        </div>

        <div className="p-6 space-y-6 max-h-[70vh] overflow-y-auto">
          {guest && (
            <>
              {/* Guest Summary Card */}
              <div className="bg-gradient-to-br from-orange-50 to-amber-50 border-2 border-orange-100 rounded-xl p-5">
                <div className="flex items-start gap-4">
                  <div className="bg-gradient-to-br from-orange-500 to-amber-500 text-white w-14 h-14 rounded-2xl flex items-center justify-center font-bold text-xl shadow-lg">
                    {guest.assigned_room_number || '?'}
                  </div>
                  <div className="flex-1">
                    <h3 className="text-lg font-bold text-gray-900 mb-2">
                      {guest.guest_name}
                    </h3>
                    <div className="grid grid-cols-2 gap-3 text-sm">
                      <div>
                        <span className="text-orange-600 font-medium">Room:</span>
                        <span className="ml-2 text-orange-900 font-bold text-lg">
                          {guest.assigned_room_number || 'N/A'}
                        </span>
                      </div>
                      <div>
                        <span className="text-orange-600 font-medium">Type:</span>
                        <span className="ml-2 text-orange-900 font-semibold">{guest.room_type || 'N/A'}</span>
                      </div>
                      <div>
                        <span className="text-orange-600 font-medium">Check-In:</span>
                        <span className="ml-2 text-orange-900 font-semibold">{guest.check_in}</span>
                      </div>
                      <div>
                        <span className="text-orange-600 font-medium">Check-Out:</span>
                        <span className="ml-2 text-orange-900 font-semibold">{guest.check_out}</span>
                      </div>
                      <div className="col-span-2">
                        <span className="text-orange-600 font-medium">Stay:</span>
                        <span className="ml-2 text-orange-900 font-semibold">{guest.nights} nights</span>
                      </div>
                    </div>
                  </div>
                </div>
              </div>

              <Separator />

              {/* Folio Details */}
              {loadingFolio ? (
                <div className="flex flex-col items-center justify-center py-12 bg-gray-50 rounded-xl">
                  <Loader2 className="w-8 h-8 animate-spin text-orange-600 mb-3" />
                  <span className="text-sm text-gray-600 font-medium">Loading folio details...</span>
                </div>
              ) : folioData ? (
                <div className="bg-gradient-to-br from-blue-50 to-indigo-50 border-2 border-blue-100 rounded-xl p-5">
                  <div className="flex items-center gap-2 mb-4">
                    <DollarSign className="w-5 h-5 text-blue-600" />
                    <h3 className="text-lg font-bold text-gray-900">Folio Summary</h3>
                  </div>
                  
                  <div className="space-y-3">
                    <div className="flex justify-between items-center py-2">
                      <span className="text-gray-700 font-medium">Room Charges</span>
                      <span className="font-bold text-gray-900 text-lg tabular-nums">
                        ${folioData.bookings?.find(b => b.id === (guest.booking_id || guest.id))?.final_price?.toFixed(2) || '0.00'}
                      </span>
                    </div>
                    
                    <Separator />
                    
                    <div className="flex justify-between items-center py-3 bg-blue-100/50 -mx-5 px-5 rounded-lg">
                      <span className="font-bold text-blue-900 text-lg">Total Amount</span>
                      <span className="font-bold text-blue-600 text-2xl tabular-nums">
                        ${folioData.bookings?.find(b => b.id === (guest.booking_id || guest.id))?.final_price?.toFixed(2) || '0.00'}
                      </span>
                    </div>
                    
                    <div className="flex items-center gap-2 text-sm text-emerald-700 bg-emerald-50 py-2 px-3 rounded-lg">
                      <CheckCircle className="w-4 h-4" />
                      <span className="font-semibold">Payment Status: Fully Settled</span>
                    </div>
                  </div>
                </div>
              ) : null}

              {/* Folio Actions */}
              <div className="bg-gradient-to-br from-purple-50 to-pink-50 border-2 border-purple-100 rounded-xl p-5">
                <p className="font-bold text-gray-900 mb-3 flex items-center gap-2">
                  <Receipt className="w-5 h-5 text-purple-600" />
                  Folio Options
                </p>
                <div className="grid grid-cols-2 gap-3">
                  <Button 
                    variant="outline" 
                    onClick={handlePrint}
                    className="h-12 border-2 border-blue-300 text-blue-600 hover:bg-blue-50 hover:border-blue-400 font-semibold"
                    disabled={loadingFolio || !folioData}
                  >
                    <Printer className="w-5 h-5 mr-2" />
                    Print Folio
                  </Button>
                  <Button 
                    variant="outline" 
                    onClick={handleEmail}
                    className="h-12 border-2 border-purple-300 text-purple-600 hover:bg-purple-50 hover:border-purple-400 font-semibold"
                    disabled={loadingFolio || !folioData || emailingSending}
                  >
                    <Mail className="w-5 h-5 mr-2" />
                    {emailingSending ? 'Sending...' : 'Email Folio'}
                  </Button>
                </div>
                <p className="text-xs text-gray-600 mt-3 text-center bg-white/50 py-2 rounded-lg">
                  Guest will receive a detailed receipt for their records
                </p>
              </div>

              {/* Checkout Reminder */}
              <div className="bg-amber-50 border-2 border-amber-200 rounded-xl p-4">
                <div className="flex items-start gap-3">
                  <div className="bg-amber-500 rounded-full p-1 mt-0.5">
                    <svg className="w-4 h-4 text-white" fill="currentColor" viewBox="0 0 20 20">
                      <path fillRule="evenodd" d="M8.257 3.099c.765-1.36 2.722-1.36 3.486 0l5.58 9.92c.75 1.334-.213 2.98-1.742 2.98H4.42c-1.53 0-2.493-1.646-1.743-2.98l5.58-9.92zM11 13a1 1 0 11-2 0 1 1 0 012 0zm-1-8a1 1 0 00-1 1v3a1 1 0 002 0V6a1 1 0 00-1-1z" clipRule="evenodd" />
                    </svg>
                  </div>
                  <div className="flex-1">
                    <p className="font-semibold text-amber-900 text-sm mb-1">Before Checkout:</p>
                    <ul className="text-xs text-amber-800 space-y-1">
                      <li>✓ Verify all charges are settled</li>
                      <li>✓ Collect room keys from guest</li>
                      <li>✓ Schedule room inspection</li>
                    </ul>
                  </div>
                </div>
              </div>
            </>
          )}
        </div>

        <div className="px-6 py-4 bg-gray-50 border-t flex justify-end gap-3">
          <Button variant="outline" onClick={() => onOpenChange(false)} disabled={loading} className="px-6">
            Cancel
          </Button>
          <Button 
            onClick={onConfirm} 
            disabled={loading}
            data-testid="confirm-checkout-btn"
            className="px-8 bg-gradient-to-r from-orange-500 to-amber-500 hover:from-orange-600 hover:to-amber-600 text-white font-semibold shadow-lg"
          >
            {loading ? (
              <>
                <Loader2 className="w-4 h-4 animate-spin mr-2" />
                Processing...
              </>
            ) : (
              <>
                <LogOut className="w-4 h-4 mr-2" />
                Confirm Check-Out
              </>
            )}
          </Button>
        </div>
      </DialogContent>
    </Dialog>
  );
};
